import React, { useState } from "react";

import { changePassword } from "../../services/memberServices";
import RightArrow from "../../components/SVG";

const ChangePasswordTabPanel = ({ memberId }) => {
  // State management
  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Password visibility state
  const [passwordVisibility, setPasswordVisibility] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear messages when user starts typing
    if (message) setMessage("");
    if (error) setError("");
  };

  // Toggle password visibility
  const togglePasswordVisibility = (fieldName) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setError("");

    // Client-side validation
    if (
      !formData.oldPassword ||
      !formData.newPassword ||
      !formData.confirmPassword
    ) {
      setError("All fields are required");
      setLoading(false);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError("New password and confirm password do not match");
      setLoading(false);
      return;
    }

    try {
      const payload = {
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
        memberId: memberId,
      };

      await changePassword(payload);
      setMessage("Password changed successfully");

      // Reset form
      setFormData({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      // Reset password visibility
      setPasswordVisibility({
        oldPassword: false,
        newPassword: false,
        confirmPassword: false,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Eye icon component
  const EyeIcon = ({ isVisible, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        position: "absolute",
        right: "15px",
        top: "50%",
        transform: "translateY(-50%)",
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: "0",
        color: "#666",
        fontSize: "18px",
      }}
      tabIndex={-1}
    >
      {isVisible ? (
        // Eye open icon (password visible)
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
        </svg>
      ) : (
        // Eye closed icon (password hidden)
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
        </svg>
      )}
    </button>
  );

  return (
    <div className="it-teacher-details-right pt-0 pb-0">
      <div className="col-xl-12 col-lg-12">
        <form onSubmit={handleSubmit}>
          <div className="it-signup-wrap">
            <h4 className="it-signup-title">Change Password</h4>

            {/* Display success/error messages */}
            {message && (
              <div
                className="alert alert-success mb-20"
                style={{
                  color: "green",
                  padding: "10px",
                  marginBottom: "20px",
                }}
              >
                {message}
              </div>
            )}
            {error && (
              <div
                className="alert alert-danger mb-20"
                style={{
                  color: "red",
                  padding: "10px",
                  marginBottom: "20px",
                }}
              >
                {error}
              </div>
            )}

            <div className="it-signup-input-wrap">
              {/* Old Password Field */}
              <div
                className="it-signup-input mb-20"
                style={{ position: "relative" }}
              >
                <input
                  type={passwordVisibility.oldPassword ? "text" : "password"}
                  name="oldPassword"
                  placeholder="Old Password"
                  value={formData.oldPassword}
                  onChange={handleInputChange}
                  disabled={loading}
                  style={{
                    border: "1px solid black",
                    borderRadius: "10px",
                    paddingRight: "50px", // Make space for eye icon
                  }}
                  required
                />
                <EyeIcon
                  isVisible={passwordVisibility.oldPassword}
                  onClick={() => togglePasswordVisibility("oldPassword")}
                />
              </div>

              {/* New Password Field */}
              <div
                className="it-signup-input mb-20"
                style={{ position: "relative" }}
              >
                <input
                  type={passwordVisibility.newPassword ? "text" : "password"}
                  name="newPassword"
                  placeholder="New Password"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  disabled={loading}
                  style={{
                    border: "1px solid black",
                    borderRadius: "10px",
                    paddingRight: "50px", // Make space for eye icon
                  }}
                  required
                />
                <EyeIcon
                  isVisible={passwordVisibility.newPassword}
                  onClick={() => togglePasswordVisibility("newPassword")}
                />
              </div>

              {/* Confirm Password Field */}
              <div
                className="it-signup-input mb-20"
                style={{ position: "relative" }}
              >
                <input
                  type={
                    passwordVisibility.confirmPassword ? "text" : "password"
                  }
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  disabled={loading}
                  style={{
                    border: "1px solid black",
                    borderRadius: "10px",
                    paddingRight: "50px", // Make space for eye icon
                  }}
                  required
                />
                <EyeIcon
                  isVisible={passwordVisibility.confirmPassword}
                  onClick={() => togglePasswordVisibility("confirmPassword")}
                />
              </div>
            </div>

            <div className="it-signup-btn d-sm-flex justify-content-between align-items-center mb-40">
              <button type="submit" className="ed-btn-theme" disabled={loading}>
                {loading ? "Changing Password..." : "Change Password"}
                <i>
                  <RightArrow />
                </i>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordTabPanel;
